import omit from 'lodash/omit';

import {
  AUTOMATION_CONNECTIONS,
  RECURRING_ANONYMIZATION,
  RECURRING_ANONYMIZATION_ENABLED,
  RECURRING_ANONYMIZATION_LAST_RUN_STATUS,
  RECURRING_ANONYMIZATION_MIN_CADENCE_DAYS,
  RUN_ANONYMIZATION_CHECK_POLL_TIMEOUT_MS,
} from '../constants/organizationsKeys';
import {
  AnonymizationRunStatusEnum,
  IAnonymizationLastRunStatus,
  IOrganizationItem,
  IOrganizationListItem,
  IOrganizationOptions,
  ISimplifiedOrganization,
  LastRunViewEnum,
  OrganizationFilterTypesEnum,
  TOrganizationsDetails,
} from '../types/organizations';

// TODO: added logic after implementation activate\deactivate organization
export const filterByActiveStatus = (tabId: number) => (/*org: IOrganizationItem*/) => {
  switch (tabId) {
    case OrganizationFilterTypesEnum.Activated:
      return true;
    case OrganizationFilterTypesEnum.Deactivated:
      return false;
    default:
      return true;
  }
};

export const sortByName =
  (isCaseSensitive: boolean = true) =>
  ({ name: firstName }: { name: string }, { name: secondName }: { name: string }) => {
    const a = isCaseSensitive ? firstName : firstName.toLocaleLowerCase();
    const b = isCaseSensitive ? secondName : secondName.toLocaleLowerCase();

    if (a < b) {
      return -1;
    }
    if (a > b) {
      return 1;
    }
    return 0;
  };

export const getSimplifiedOrganization = ({
  uid,
  name,
  org_options,
  org_departments,
  delayed_shipping_page: { logo_url },
}: IOrganizationItem): ISimplifiedOrganization => ({
  uid,
  name,
  org_options,
  org_departments,
  logo_url,
});

export const removeUntrackedOrgFields = ({
  org_options: orgOptions,
  ...restOrg
}: {
  org_options?: IOrganizationOptions;
}) => {
  const trackedOrgOptions = omit(orgOptions, AUTOMATION_CONNECTIONS);
  const schedule = orgOptions?.[RECURRING_ANONYMIZATION];

  return {
    ...restOrg,
    org_options: schedule
      ? { ...trackedOrgOptions, [RECURRING_ANONYMIZATION]: omit(schedule, RECURRING_ANONYMIZATION_LAST_RUN_STATUS) }
      : trackedOrgOptions,
  };
};

export const mergeOrganizationOptions = <T extends Partial<IOrganizationOptions>>(
  existing: T | null | undefined,
  incoming: Partial<IOrganizationOptions> | null | undefined,
): T => {
  const existingSchedule = existing?.[RECURRING_ANONYMIZATION];
  const incomingSchedule = incoming?.[RECURRING_ANONYMIZATION];

  return {
    ...existing,
    ...incoming,
    ...(existingSchedule || incomingSchedule
      ? { [RECURRING_ANONYMIZATION]: { ...existingSchedule, ...incomingSchedule } }
      : {}),
  } as T;
};

export const getAnonymizationLastRunStatus = (
  organization?: IOrganizationListItem | null,
): IAnonymizationLastRunStatus | null =>
  organization?.org_options?.[RECURRING_ANONYMIZATION]?.[RECURRING_ANONYMIZATION_LAST_RUN_STATUS] ?? null;

export const getLastRunView = (lastRun: IAnonymizationLastRunStatus | null): LastRunViewEnum => {
  if (!lastRun) {
    return LastRunViewEnum.None;
  }

  if (lastRun.status === AnonymizationRunStatusEnum.Failed) {
    return LastRunViewEnum.Failed;
  }

  return lastRun.number_sends > 0 ? LastRunViewEnum.Completed : LastRunViewEnum.NoMatches;
};

export const isCadenceUnsetByBackend = (cadenceDays?: number | null) => cadenceDays === 0;

export const isValidCadence = (value?: number | null): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= RECURRING_ANONYMIZATION_MIN_CADENCE_DAYS;

export const pickNewerLastRunStatus = (
  existing: IAnonymizationLastRunStatus | null | undefined,
  incoming: IAnonymizationLastRunStatus | null | undefined,
): IAnonymizationLastRunStatus | null | undefined => {
  if (!existing) {
    return incoming;
  }

  if (!incoming) {
    return existing;
  }

  const existingMs = Date.parse(existing.timestamp);
  const incomingMs = Date.parse(incoming.timestamp);

  if (!Number.isFinite(incomingMs)) {
    return Number.isFinite(existingMs) ? existing : incoming;
  }

  return Number.isFinite(existingMs) && existingMs > incomingMs ? existing : incoming;
};

export const keepNewerLastRunStatuses = (
  incoming: IOrganizationItem[],
  existing: IOrganizationItem[] | null | undefined,
): IOrganizationItem[] => {
  if (!existing?.length) {
    return incoming;
  }

  const existingById = new Map(existing.map((organization) => [organization.uid, organization]));

  return incoming.map((organization) => {
    const incomingSchedule = organization.org_options?.[RECURRING_ANONYMIZATION];

    if (!incomingSchedule) {
      return organization;
    }

    const incomingStatus = incomingSchedule[RECURRING_ANONYMIZATION_LAST_RUN_STATUS];
    const kept = pickNewerLastRunStatus(
      getAnonymizationLastRunStatus(existingById.get(organization.uid)),
      incomingStatus,
    );

    if (kept === incomingStatus || kept === undefined) {
      return organization;
    }

    return {
      ...organization,
      org_options: {
        ...organization.org_options,
        [RECURRING_ANONYMIZATION]: { ...incomingSchedule, [RECURRING_ANONYMIZATION_LAST_RUN_STATUS]: kept },
      },
    };
  });
};

export const isNewerAnonymizationRun = (
  lastRunStatus: IAnonymizationLastRunStatus | null | undefined,
  baselineTimestamp: string | null,
): lastRunStatus is IAnonymizationLastRunStatus => {
  if (!lastRunStatus?.timestamp) {
    return false;
  }

  if (!baselineTimestamp) {
    return true;
  }

  return Date.parse(lastRunStatus.timestamp) > Date.parse(baselineTimestamp);
};

export const getRemainingAnonymizationPollMs = (startedAt: string, now: number): number => {
  const startedAtMs = Date.parse(startedAt);

  if (!Number.isFinite(startedAtMs)) {
    return 0;
  }

  return Math.max(
    0,
    Math.min(RUN_ANONYMIZATION_CHECK_POLL_TIMEOUT_MS, startedAtMs + RUN_ANONYMIZATION_CHECK_POLL_TIMEOUT_MS - now),
  );
};

export const isRecurringAnonymizationBeingEnabled = (
  saved: TOrganizationsDetails | null | undefined,
  values: TOrganizationsDetails | null | undefined,
) =>
  !saved?.org_options?.[RECURRING_ANONYMIZATION]?.[RECURRING_ANONYMIZATION_ENABLED] &&
  !!values?.org_options?.[RECURRING_ANONYMIZATION]?.[RECURRING_ANONYMIZATION_ENABLED];
